import { withFlightHttp } from '../debug/flightHttp'
import { isFlightRecorderEnabled } from '../debug/flightRecorder'

function isFakeEnabled () {
  return String(process.env.REACT_APP_FAKE_SEAT_BLOCK || '').trim() === '1'
}

function getFakeScenario () {
  if (typeof window !== 'undefined' && window.__seatBlockFakeScenario) {
    return String(window.__seatBlockFakeScenario)
  }
  return 'ok'
}

function delay (ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function seatTakenError () {
  const err = new Error('SEAT_TAKEN')
  err.response = {
    status: 409,
    data: {
      message: {
        code: 'SEAT_TAKEN',
        message: 'There is no available capacity or some seats were booked.',
      },
    },
  }
  return err
}

function withFakeFlightHttp (method, sessionId, op, body, run) {
  if (!isFlightRecorderEnabled()) return run()
  return withFlightHttp({
    method,
    url: `v3/booking/${sessionId}/${op}`,
    fullUrl: `v3/booking/${sessionId}/${op}`,
    body,
    run,
  })
}

/**
 * @param {{ scenario?: string }} [options]
 */
export function createFakeSeatBlockClient (options = {}) {
  const log = []
  if (typeof window !== 'undefined') {
    window.__seatBlockFakeLog = log
  }

  const resolveScenario = () => options.scenario || getFakeScenario()

  return {
    async block (sessionId, body) {
      return withFakeFlightHttp('POST', sessionId, 'block', body, async () => {
        const scenario = resolveScenario()
        const entry = { op: 'block', sessionId, body, ts: Date.now(), scenario }
        log.push(entry)

        if (scenario === 'slow') await delay(50)
        if (scenario === 'seat_taken_on_fare' && body.fareId) {
          throw seatTakenError()
        }

        // Kiosk owns the UUID: echo it back, never mint one here.
        const syncEntryId = body.syncEntryId ? String(body.syncEntryId) : null

        return {
          status: 200,
          data: {
            error: false,
            data: {
              seatId: body.seatId,
              step: body.step,
              fareId: body.fareId ?? null,
              syncEntryId,
              ttl_sec: 300,
              expires_at: new Date(Date.now() + 300000).toISOString(),
            },
          },
        }
      })
    },

    async unblock (sessionId, body) {
      return withFakeFlightHttp('POST', sessionId, 'unblock', body, async () => {
        const entry = { op: 'unblock', sessionId, body, ts: Date.now(), scenario: resolveScenario() }
        log.push(entry)
        return {
          status: 200,
          data: {
            error: false,
            data: {
              seatId: body.seatId,
              step: body.step,
              released: true,
            },
          },
        }
      })
    },

    getLog () {
      return log
    },
  }
}

/**
 * @param {(url: string, body: object) => Promise<any>} postFn
 */
export function createHttpSeatBlockClient (postFn) {
  return {
    block (sessionId, body) {
      return postFn(`v3/booking/${sessionId}/block`, body)
    },
    unblock (sessionId, body) {
      return postFn(`v3/booking/${sessionId}/unblock`, body)
    },
  }
}

let _cachedClient = null

/**
 * Reset cached client (tests / flag changes).
 */
export function resetSeatBlockClientCache () {
  _cachedClient = null
}

/**
 * @param {(url: string, body: object) => Promise<any>} postFn
 */
export function getSeatBlockClient (postFn) {
  if (_cachedClient) return _cachedClient
  _cachedClient = isFakeEnabled()
    ? createFakeSeatBlockClient()
    : createHttpSeatBlockClient(postFn)
  return _cachedClient
}

/** Convenience used by callers that already have a client */
export { buildBlockPayload, buildUnblockPayload } from '../helpers/seatBlockPayload'
